import axios from '@fs/zion-axios'

const BASE_USERS_PATH = '/service/dmc/users'
const BASE_SESSION_PATH = '/service/dmc/session'
const BASE_AUTHN_PATH = '/service/dmc/authn'

export default async function getUserSummary(userId, context, forward) {
  const response = await axios.get(`${BASE_USERS_PATH}/${userId}/summary`, {
    params: { context: context || null, forward },
  })
  return response
}

export function getCisIdAndContributorIdPair(id, type) {
  // type must be contributorId or cisId
  const params = {
    id,
    type,
  }
  return axios.get(`${BASE_USERS_PATH}/id-pairs/id-pair`, { params })
}

export async function getCisIdFromUsername(username) {
  // Must keep the last slash in the url because of '.' in usernames. Ex: carlos.campos
  return axios.get(`${BASE_USERS_PATH}/search/username/${username}/`)
}

export async function getUserWithUsername(username) {
  // Must keep the last slash in the url because of '.' in usernames. Ex: carlos.campos
  const response = await axios.get(`${BASE_USERS_PATH}?username=${username}`)
  return response
}

export async function getUser(userId, type) {
  if (!userId) {
    return Promise.reject(new Error('User ID is not defined'))
  }
  const response = await axios.get(`${BASE_USERS_PATH}/${userId}?type=${type}`)
  return response
}

export async function getUserMetadata(userId, context) {
  const contextParam = context ? `?context=${context}` : ``
  const response = await axios.get(`${BASE_USERS_PATH}/${userId}/metadata${contextParam}`)
  return response
}

export async function getCmisReferences({ id, context }) {
  const contextParam = context ? `?context=${context}` : ``
  const response = await axios.get(`${BASE_USERS_PATH}/${id}/references${contextParam}`)
  return response
}

export async function getContactNameSearchResult(contactName) {
  const response = await axios.get(`${BASE_USERS_PATH}/search/contactName/${contactName}`)
  return response
}

export async function getContributorIdFromSpaceId(spaceId) {
  const response = await axios.get(`${BASE_USERS_PATH}/space/${spaceId}/user`)
  return response
}

export async function getPrivatePersons(cisId, groupId, pageSize, nextPageToken = null, nameFilter = null) {
  const params = {
    cisId,
    groupId,
    pageSize: nameFilter ? 'all' : pageSize,
    nameFilter,
    nextPageToken,
  }
  return axios.get(`${BASE_USERS_PATH}/owner/persons`, { params })
}

export async function getTreePersonsCount(cisId) {
  const params = {
    cisId,
  }
  return axios.get(`${BASE_USERS_PATH}/owner/persons/count`, { params })
}

export async function getUserReservationData(userId, start, count, showOnlyCompletedReservations) {
  const response = await axios.get(
    `${BASE_USERS_PATH}/${userId}/reservations?start=${start}&count=${count}&showOnlyCompletedReservations=${showOnlyCompletedReservations}`
  )
  return response
}

export async function downloadUserReservationData(
  userId,
  showOnlyCompletedReservations,
  sharedType,
  retryChunkIndexes
) {
  const params = new URLSearchParams({
    showOnlyCompletedReservations,
    sharedType,
  })
  if (retryChunkIndexes && retryChunkIndexes.length > 0) {
    retryChunkIndexes.forEach((idx) => params.append('retryChunkIndexes', idx))
  }
  const response = await axios.get(`${BASE_USERS_PATH}/${userId}/downloadReservations?${params}`)
  return response
}

export async function getUserCtrData(userId, context) {
  const contextParam = context ? `?context=${context}` : ``
  const response = await axios.get(`${BASE_USERS_PATH}/${userId}/ctr${contextParam}`)
  return response
}

export function getUserGroups(memberCisId, ownerId) {
  const params = {
    ownerId,
  }
  return axios.get(`${BASE_USERS_PATH}/${memberCisId}/groups`, { params })
}

export function getSessionUserId() {
  const config = {
    headers: {
      Accept: 'application/session-v1+json',
    },
  }
  return axios.get(`${BASE_SESSION_PATH}/current`, config)
}

export function getUserContributions(userId, pageSize, fromCommandId, personId, type, subtypes) {
  pageSize = pageSize || 100
  let url = `${BASE_USERS_PATH}/${userId}/contributions?pageSize=${pageSize}`
  if (fromCommandId) {
    url = `${url}&fromCommandId=${fromCommandId}`
  }
  if (personId) {
    url = `${url}&personIdFilter=${personId}`
  }
  if (type && subtypes) {
    url = `${url}&type=${type}&subtype=${subtypes.join('&subtype=')}`
  }
  return axios.get(url).then((response) => {
    return response.data
  })
}

export function getUserSubContributions(userId, commandUuid) {
  return axios.get(`${BASE_USERS_PATH}/${userId}/subContributions?commandUuid=${commandUuid}`).then((response) => {
    return response.data
  })
}

export function getUserMessagesThreads(cisId, participant, startDate, endDate, currentPage, pageSize) {
  let url = `${BASE_USERS_PATH}/messageThreads/filter?cisId=${cisId}`
  if (participant != null) {
    url = `${url}&participantId=${participant}`
  }
  if (startDate != null) {
    url = `${url}&startDate=${startDate}`
  }
  if (endDate != null) {
    url = `${url}&endDate=${endDate}`
  }
  if (currentPage != null) {
    url = `${url}&currentPage=${currentPage}`
  }
  if (pageSize != null) {
    url = `${url}&pageSize=${pageSize}`
  }
  return axios.get(url).then((response) => {
    return response.data
  })
}

export function clearContactNameCache(cisId) {
  return axios.post(`${BASE_USERS_PATH}/contacts?cisId=${cisId}`)
}

export function getUserProfiles(contactId, profileName, countryId, stateOrProvinceId) {
  const params = {
    contactId,
    profileName,
    countryId,
    stateOrProvinceId,
  }
  return axios.get(`${BASE_USERS_PATH}/profiles`, { params })
}

export function accumulateDestructionReportData(userId, dmcDestructionReportData) {
  return axios.put(`${BASE_USERS_PATH}/${userId}/destructionReportData`, dmcDestructionReportData || {})
}

export function calculateDestructionReport(userId, dmcDestructionReportData) {
  return axios.put(`${BASE_USERS_PATH}/${userId}/calculateDestructionReport`, dmcDestructionReportData)
}

export function getUserRollbackSummary(userId) {
  return axios.get(`${BASE_USERS_PATH}/${userId}/rollback/summary`)
}

export function getUserRollbackResults(userId) {
  return axios.get(`${BASE_USERS_PATH}/${userId}/rollback/results`)
}

export function userRollback(userId, cutoffTimestamp, rollbackAgain) {
  return axios.delete(
    `${BASE_USERS_PATH}/${userId}/rollback?rollbackAgain=${rollbackAgain}${
      cutoffTimestamp ? `&cutoffTimestamp=${cutoffTimestamp}` : ''
    }`
  )
}

export function getOwnedTreesForUser(cisUserId) {
  return axios.get(`${BASE_USERS_PATH}/${cisUserId}/owned-trees`)
}

export function getParticipantTreesForUser(cisUserId) {
  return axios.get(`${BASE_USERS_PATH}/${cisUserId}/participant-trees`)
}

export function getTreeChanges(treeId) {
  return axios.get(`${BASE_USERS_PATH}/trees/${treeId}/changes`)
}

export function getUserJurisdictions(userId) {
  return axios.get(`${BASE_AUTHN_PATH}/${userId}/jurisdictions`)
}
